import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import Header from '../components/organisms/Header';
import { getCandidateProfile } from '../services/api';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';
import useCredits from '../hooks/useCredits';

const CandidateProfileViewPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState('education');
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  // Credit system
  const { balance, loading: creditsLoading } = useCredits();

  useEffect(() => {
    loadProfile();
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      const token = candidateSession?.access_token;
      
      if (!candidateId) return;
      
      const response = await fetch(`${API_BASE_URL}/verification/status/${candidateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data);
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      const loginEmail = candidateSession?.email || '';

      if (!candidateId && !loginEmail) {
        navigate('/login');
        return;
      }

      const profile = await getCandidateProfile(candidateId, loginEmail);
      
      console.log('🔍 Profile received from API:', profile);
      console.log('🔍 Profile keys:', profile ? Object.keys(profile) : 'null');
      
      if (profile) {
        // Build profile data from database fields
        const profileStructure = {
          personalInfo: {
            name: profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
            email: profile.email || loginEmail,
            phone: profile.phone_number || profile.phone || '',
            title: profile.job_role || 'Professional',
            location: profile.location || '',
            nationality: profile.nationality || '',
            nationalId: profile.national_id || '',
            gender: profile.gender || '',
            dob: profile.dob || ''
          },
          skills: Array.isArray(profile.skills) ? profile.skills : [],
          languages: Array.isArray(profile.languages) ? profile.languages : [],
          education: Array.isArray(profile.education) ? profile.education : [],
          workExperience: Array.isArray(profile.work_experience) ? profile.work_experience : [],
          projects: Array.isArray(profile.projects) ? profile.projects : [],
          certifications: Array.isArray(profile.courses) ? profile.courses : [],
          hobbies: profile.hobbies ? profile.hobbies.split(',').map(h => h.trim()).filter(h => h) : [],
          links: {
            linkedin: profile.linkedin || '',
            github: profile.github || '',
            portfolio: profile.portfolio || ''
          },
          resumeUrl: profile.resume_url || null,
          resumeFilename: profile.resume_filename || null
        };
        
        console.log('✅ Profile structure built:', profileStructure);
        console.log('✅ Skills:', profileStructure.skills);
        console.log('✅ Education:', profileStructure.education);
        console.log('✅ Work Experience:', profileStructure.workExperience);
        
        setProfileData(profileStructure);
        
        // Check if profile is empty (no meaningful data)
        const hasData = 
          (profileStructure.skills && profileStructure.skills.length > 0) ||
          (profileStructure.education && profileStructure.education.length > 0) ||
          (profileStructure.workExperience && profileStructure.workExperience.length > 0) ||
          (profileStructure.projects && profileStructure.projects.length > 0) ||
          profileStructure.resumeUrl;
        
        if (!hasData) {
          console.log('Profile is empty, redirecting to onboarding');
          navigate('/onboarding');
          return;
        }
      } else {
        // No profile found, redirect to onboarding
        console.log('No profile found, redirecting to onboarding');
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Show basic profile even on error
      const candidateSession = SessionManager.getSession('candidate');
      const fallbackData = {
        personalInfo: {
          name: candidateSession?.full_name || 'User',
          email: candidateSession?.email || '',
          phone: '',
          title: 'Professional',
          location: '',
          nationality: '',
          nationalId: '',
          gender: '',
          dob: ''
        },
        skills: [],
        education: [],
        workExperience: [],
        projects: [],
        certifications: [],
        languages: [],
        hobbies: [],
        tools: [],
        links: { linkedin: '', portfolio: '', github: '' }
      };
      setProfileData(fallbackData);
    } finally {
      setLoading(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsUploadingResume(true);

    try {
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('candidate_id', candidateId);
      formData.append('email', profileData.personalInfo.email);
      formData.append('full_name', profileData.personalInfo.name);
      formData.append('phone', profileData.personalInfo.phone);
      formData.append('resume', file);
      
      // Upload resume
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
      const token = candidateSession?.access_token;
      
      const response = await fetch(`${API_BASE_URL}/candidates/profile`, {
        method: 'PUT',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload resume');
      }
      
      alert('Resume uploaded successfully!');
      // Reload profile to show updated resume
      await loadProfile();
    } catch (error) {
      console.error('Error uploading resume:', error);
      alert(`Failed to upload resume: ${error.message}`);
    } finally {
      setIsUploadingResume(false);
    }
  };

  if (loading) {
    return (
      <div className="relative flex min-h-screen flex-col bg-gray-50">
        <Header />
        <main className="flex-1 py-12 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="relative flex min-h-screen flex-col bg-gray-50">
        <Header />
        <main className="flex-1 py-12 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-600">No profile data available</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50">
      <Header />
      <main className="flex-1 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header with Edit Button */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/profile/setup?mode=manual')}
                className="text-gray-600 hover:text-gray-900"
                title="Edit Profile"
              >
                <Icon name="arrow_back" />
              </button>
              <h1 className="text-3xl font-bold text-gray-900">User Profile</h1>
            </div>
            <div className="flex gap-3">
              {/* Credit Balance Display */}
              <div className="px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.736 6.979C9.208 6.193 9.696 6 10 6c.304 0 .792.193 1.264.979a1 1 0 001.715-1.029C12.279 4.784 11.232 4 10 4s-2.279.784-2.979 1.95c-.285.475-.507 1-.67 1.55H6a1 1 0 000 2h.013a9.358 9.358 0 000 1H6a1 1 0 100 2h.351c.163.55.385 1.075.67 1.55C7.721 15.216 8.768 16 10 16s2.279-.784 2.979-1.95a1 1 0 10-1.715-1.029c-.472.786-.96.979-1.264.979-.304 0-.792-.193-1.264-.979a4.265 4.265 0 01-.264-.521H10a1 1 0 100-2H8.017a7.36 7.36 0 010-1H10a1 1 0 100-2H8.472c.08-.185.167-.36.264-.521z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Credits</p>
                  <p className="text-sm font-bold text-purple-700">{creditsLoading ? '...' : balance}</p>
                </div>
              </div>
              
              {/* Verify Profile Button - Only show if not verified */}
              {!verificationStatus?.verified && (
                <button 
                  onClick={() => navigate('/verification-welcome')}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg flex items-center gap-2 hover:from-blue-700 hover:to-purple-700"
                >
                  <Icon name="verified_user" />
                  Verify Profile
                </button>
              )}
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50">
                <Icon name="description" />
                Preview as CV
              </button>
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg flex items-center gap-2 hover:bg-gray-50">
                <Icon name="share" />
                Share Profile
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Profile Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Profile Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => navigate('/profile/setup?mode=manual')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Icon name="edit" className="text-sm" />
                    Edit Profile
                  </button>
                </div>

                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 mb-4 overflow-hidden">
                    {profileData.photo ? (
                      <img src={profileData.photo} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
                        {profileData.personalInfo?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {profileData.personalInfo?.name || 'User Name'}
                  </h2>
                  
                  {/* Verification Badge */}
                  {verificationStatus?.verified && (
                    <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-full mb-2">
                      <Icon name="verified" size={20} className="text-green-600" />
                      <span className="text-sm font-semibold text-green-600">Verified Profile</span>
                    </div>
                  )}

                  <p className="text-gray-600 mb-1">
                    {profileData.personalInfo?.title || 'Student'} | {profileData.personalInfo?.location || 'Location'}
                  </p>
                  <p className="text-sm text-gray-500 mb-1">{profileData.personalInfo?.email || ''}</p>
                  <p className="text-sm text-gray-500">{profileData.personalInfo?.phone || ''}</p>
                </div>

                {/* Personal Information */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Personal Information:</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Icon name="public" className="text-gray-400" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Nationality</p>
                        <p className="text-sm font-medium">{profileData.personalInfo?.nationality || 'N/A'}</p>
                      </div>
                      <Icon name="badge" className="text-gray-400" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">National ID</p>
                        <p className="text-sm font-medium">{profileData.personalInfo?.nationalId || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Icon name="person" className="text-gray-400" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Gender</p>
                        <p className="text-sm font-medium">{profileData.personalInfo?.gender || 'N/A'}</p>
                      </div>
                      <Icon name="calendar_today" className="text-gray-400" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">Date of Birth</p>
                        <p className="text-sm font-medium">{profileData.personalInfo?.dob || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Skills:</h3>
                  {profileData.skills && profileData.skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profileData.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No skills specified</p>
                  )}
                </div>

                {/* External Links */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">External Links</h3>
                  {(profileData.links?.linkedin || profileData.links?.github || profileData.links?.portfolio) ? (
                    <div className="space-y-3">
                      {profileData.links?.linkedin && (
                        <a 
                          href={profileData.links.linkedin.startsWith('http') ? profileData.links.linkedin : `https://${profileData.links.linkedin}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
                        >
                          <Icon name="link" className="text-blue-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-1">LinkedIn</p>
                            <p className="text-sm font-medium text-blue-600 truncate group-hover:text-blue-700">
                              {profileData.links.linkedin}
                            </p>
                          </div>
                          <Icon name="open_in_new" size={18} className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      )}
                      {profileData.links?.github && (
                        <a 
                          href={profileData.links.github.startsWith('http') ? profileData.links.github : `https://${profileData.links.github}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                        >
                          <Icon name="code" className="text-gray-700" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-1">GitHub</p>
                            <p className="text-sm font-medium text-gray-700 truncate group-hover:text-gray-900">
                              {profileData.links.github}
                            </p>
                          </div>
                          <Icon name="open_in_new" size={18} className="text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      )}
                      {profileData.links?.portfolio && (
                        <a 
                          href={profileData.links.portfolio.startsWith('http') ? profileData.links.portfolio : `https://${profileData.links.portfolio}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors group"
                        >
                          <Icon name="language" className="text-purple-600" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 mb-1">Portfolio</p>
                            <p className="text-sm font-medium text-purple-600 truncate group-hover:text-purple-700">
                              {profileData.links.portfolio}
                            </p>
                          </div>
                          <Icon name="open_in_new" size={18} className="text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No external links provided</p>
                  )}
                </div>

                {/* Resume Upload Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Resume</h3>
                  
                  {profileData.resumeUrl || profileData.resumeFilename ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Icon name="description" className="text-green-600" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {profileData.resumeFilename || 'Resume uploaded'}
                          </p>
                          <p className="text-xs text-gray-500">Current resume</p>
                        </div>
                        {profileData.resumeUrl && (
                          <button
                            onClick={() => {
                              // Handle base64 data URI
                              if (profileData.resumeUrl.startsWith('data:')) {
                                // Open base64 PDF in new window
                                const newWindow = window.open('', '_blank');
                                if (newWindow) {
                                  newWindow.document.write(`
                                    <html>
                                      <head>
                                        <title>${profileData.resumeFilename || 'Resume'}</title>
                                        <style>
                                          body { margin: 0; padding: 0; }
                                          iframe { width: 100%; height: 100vh; border: none; }
                                        </style>
                                      </head>
                                      <body>
                                        <iframe src="${profileData.resumeUrl}"></iframe>
                                      </body>
                                    </html>
                                  `);
                                  newWindow.document.close();
                                }
                              } else {
                                // Regular URL - open directly
                                window.open(profileData.resumeUrl, '_blank');
                              }
                            }}
                            className="text-blue-600 hover:text-blue-700 p-1"
                            title="View Resume"
                          >
                            <Icon name="open_in_new" size={20} />
                          </button>
                        )}
                      </div>
                      
                      <div className="relative">
                        <input
                          type="file"
                          id="resume-upload-update"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeUpload}
                          className="hidden"
                          disabled={isUploadingResume}
                        />
                        <label
                          htmlFor="resume-upload-update"
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer text-sm font-medium text-gray-700"
                        >
                          <Icon name="upload" size={18} />
                          {isUploadingResume ? 'Uploading...' : 'Update Resume'}
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 flex items-center gap-2">
                          <Icon name="info" size={18} />
                          No resume uploaded yet
                        </p>
                      </div>
                      
                      <div className="relative">
                        <input
                          type="file"
                          id="resume-upload-new"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeUpload}
                          className="hidden"
                          disabled={isUploadingResume}
                        />
                        <label
                          htmlFor="resume-upload-new"
                          className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer font-medium"
                        >
                          <Icon name="upload_file" size={20} />
                          {isUploadingResume ? 'Uploading...' : 'Upload Resume'}
                        </label>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          PDF, DOC, or DOCX (max 5MB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Detailed Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Languages */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Languages:</h3>
                {profileData.languages && profileData.languages.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {profileData.languages.map((lang, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {typeof lang === 'string' ? lang : lang.name || lang}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No languages specified</p>
                )}
              </div>

              {/* Hobbies & Interests */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Hobbies & Interests:</h3>
                {profileData.hobbies && profileData.hobbies.length > 0 ? (
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    {profileData.hobbies.map((hobby, index) => (
                      <li key={index}>{hobby}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No hobbies specified</p>
                )}
              </div>

              {/* Tabs for Education, Work, Projects */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="border-b border-gray-200">
                  <div className="flex">
                    <button
                      onClick={() => setActiveTab('education')}
                      className={`px-6 py-4 font-medium ${
                        activeTab === 'education'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Education
                    </button>
                    <button
                      onClick={() => setActiveTab('work')}
                      className={`px-6 py-4 font-medium ${
                        activeTab === 'work'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Work Experience
                    </button>
                    <button
                      onClick={() => setActiveTab('projects')}
                      className={`px-6 py-4 font-medium ${
                        activeTab === 'projects'
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Project Experience
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {activeTab === 'education' && (
                    <div className="space-y-6">
                      {profileData.education && profileData.education.length > 0 ? (
                        profileData.education.map((edu, index) => {
                          const institution = edu.institution === 'Other' ? edu.institutionOther : edu.institution;
                          const degree = edu.degree === 'Other' ? edu.degreeOther : edu.degree;
                          const field = edu.field === 'Other' ? edu.fieldOther : edu.field;
                          
                          return (
                            <div key={index} className="flex gap-4">
                              <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <Icon name="school" className="text-blue-600" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">{institution || 'Institution'}</h4>
                                <p className="text-sm text-gray-600">
                                  {degree || 'Degree'} {field ? `in ${field}` : ''}
                                </p>
                                {edu.gpa && <p className="text-sm text-gray-500">GPA: {edu.gpa}</p>}
                                <p className="text-sm text-gray-500">
                                  {edu.startDate && edu.endDate ? `${edu.startDate} - ${edu.endDate}` : ''}
                                  {edu.location ? ` | ${edu.location}` : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="school" size={48} className="text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No education history added yet</p>
                          <button
                            onClick={() => navigate('/profile/setup?mode=manual')}
                            className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Add Education
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'work' && (
                    <div className="space-y-6">
                      {profileData.workExperience && profileData.workExperience.length > 0 ? (
                        profileData.workExperience.map((work, index) => (
                          <div key={index} className="flex gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <Icon name="work" className="text-green-600" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{work.company || 'Company'}</h4>
                              <p className="text-sm text-gray-600">{work.position || 'Position'}</p>
                              <p className="text-sm text-gray-500">
                                {work.startDate && (work.current ? `${work.startDate} - Present` : `${work.startDate} - ${work.endDate || ''}`)}
                                {work.location ? ` | ${work.location}` : ''}
                              </p>
                              {work.description && (
                                <p className="mt-2 text-sm text-gray-700">{work.description}</p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="work" size={48} className="text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No work experience added yet</p>
                          <button
                            onClick={() => navigate('/profile/setup?mode=manual')}
                            className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Add Work Experience
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'projects' && (
                    <div className="space-y-6">
                      {profileData.projects && profileData.projects.length > 0 ? (
                        profileData.projects.map((project, index) => (
                          <div key={index} className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold text-gray-900">{project.title || 'Project'}</h4>
                            <p className="text-sm text-gray-600 mt-1">{project.description || ''}</p>
                            {project.technologies && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {project.technologies.split(',').map((tech, i) => (
                                  <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                    {tech.trim()}
                                  </span>
                                ))}
                              </div>
                            )}
                            {project.link && (
                              <a
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-700 mt-2 inline-flex items-center gap-1"
                              >
                                View Project <Icon name="open_in_new" size={14} />
                              </a>
                            )}
                            {(project.startDate || project.endDate) && (
                              <p className="text-xs text-gray-500 mt-2">
                                {project.startDate} {project.endDate ? `- ${project.endDate}` : ''}
                              </p>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <Icon name="code" size={48} className="text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No projects added yet</p>
                          <button
                            onClick={() => navigate('/profile/setup?mode=manual')}
                            className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Add Projects
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Tools & Certifications */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Tools</h3>
                  {profileData.tools && profileData.tools.length > 0 ? (
                    <div className="space-y-3">
                      {profileData.tools.map((tool, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-gray-700">{tool.name || tool}</span>
                          {tool.level && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              tool.level === 'Advanced' ? 'bg-green-100 text-green-700' :
                              tool.level === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {tool.level}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No tools specified</p>
                  )}
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Certifications</h3>
                  {profileData.certifications && profileData.certifications.length > 0 ? (
                    <div className="space-y-4">
                      {profileData.certifications.map((cert, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon name="workspace_premium" className="text-orange-600" size={20} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{cert.name || 'Certification'}</h4>
                            {cert.provider && <p className="text-sm text-gray-600">{cert.provider}</p>}
                            {cert.completionDate && <p className="text-xs text-gray-500">{cert.completionDate}</p>}
                            {cert.certificateUrl && (
                              <a
                                href={cert.certificateUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 mt-1"
                              >
                                View Certificate <Icon name="open_in_new" size={12} />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No certifications added yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CandidateProfileViewPage;
